import React, { useState, useRef, useEffect } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix('talent_matching.filters.applied');

const MAX_COLLAPSED_HEIGHT = 100;

type Props = {
  chips: React.ReactNode[];
};

const BaseAppliedFiltersRow = ({ chips }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const [visibleChipCount, setVisibleChipCount] = useState<number | null>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setVisibleChipCount(null);
    setIsExpanded(false);
  }, [chips.length]);

  useEffect(() => {
    // Measure how many chips fit in ~3 rows so we can collapse the rest.
    // overflow: hidden can't be used because tooltips would be clipped.
    if (!measureRef.current || visibleChipCount !== null) {
      return;
    }

    let rafId: number;
    const timer = setTimeout(() => {
      rafId = requestAnimationFrame(() => {
        if (measureRef.current) {
          const children = Array.from(measureRef.current.children);
          let count = 0;

          for (let i = 0; i < children.length; i++) {
            const child = children[i] as HTMLElement;
            const rect = child.getBoundingClientRect();
            const childBottom =
              rect.top +
              rect.height -
              measureRef.current.getBoundingClientRect().top;

            if (childBottom > MAX_COLLAPSED_HEIGHT) {
              break;
            }
            count++;
          }

          setVisibleChipCount(count);
          setNeedsExpansion(count < chips.length);
        }
      });
    }, 0);

    return () => {
      clearTimeout(timer);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [chips.length, visibleChipCount]);

  if (chips.length === 0) {
    return null;
  }

  const chipsToShow =
    isExpanded || visibleChipCount === null
      ? chips
      : chips.slice(0, visibleChipCount);

  return (
    <Stack spacing={1} sx={{ marginTop: 2 }}>
      <div
        ref={measureRef}
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 0,
          visibility: visibleChipCount === null ? 'visible' : 'hidden',
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '8px',
          width: stackRef.current?.offsetWidth || '100%',
        }}
      >
        {chips}
      </div>

      <Stack
        ref={stackRef}
        spacing={1}
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        sx={{ opacity: visibleChipCount === null ? 0 : 1 }}
      >
        {chipsToShow}
      </Stack>

      {needsExpansion && (
        <Stack
          alignItems="start"
          sx={{ opacity: visibleChipCount === null ? 0 : 1 }}
        >
          <TertiaryButton size="sm" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? t('show_less') : t('show_more')}
          </TertiaryButton>
        </Stack>
      )}
    </Stack>
  );
};

export default BaseAppliedFiltersRow;
